import { useState, useCallback, useEffect, useRef } from 'react';
import { subtasksApi } from '../api';
import { offlineStorage } from '../utils/offlineStorage';
import { useAuth } from '../contexts/AuthContext';

export function useSubtasks(taskId, initialSubtasks = []) {
  const { user } = useAuth();
  const isOnline = !!user;

  const [subtasks, setSubtasks] = useState(initialSubtasks);
  const [loading,  setLoading]  = useState(false);
  const fetchedRef = useRef(false);
  const mutatedRef = useRef(false);

  // Reset everything when switching to a different task
  useEffect(() => {
    setSubtasks(initialSubtasks);
    fetchedRef.current = false;
    mutatedRef.current = false;
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep in sync with parent ONLY before any local mutation has happened
  useEffect(() => {
    if (!fetchedRef.current && !mutatedRef.current) {
      setSubtasks(initialSubtasks);
    }
  }, [initialSubtasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFromApi = useCallback(async () => {
    if (!taskId || !isOnline || fetchedRef.current) return;
    setLoading(true);
    try {
      const res = await subtasksApi.list(taskId);
      const list = Array.isArray(res?.data?.data) ? res.data.data
                 : Array.isArray(res?.data)        ? res.data
                 : [];
      setSubtasks(list);
      fetchedRef.current = true;
      mutatedRef.current = false;
    } catch (e) {
      console.error('[useSubtasks] fetchFromApi error:', e);
    } finally {
      setLoading(false);
    }
  }, [taskId, isOnline]);

  const refresh = useCallback(async () => {
    if (!taskId || !isOnline) return;
    setLoading(true);
    try {
      const res = await subtasksApi.list(taskId);
      const list = Array.isArray(res?.data?.data) ? res.data.data
                 : Array.isArray(res?.data)        ? res.data
                 : [];
      setSubtasks(list);
      fetchedRef.current = true;
      mutatedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [taskId, isOnline]);

  const addSubtask = useCallback(async (title) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    mutatedRef.current = true;
    if (isOnline) {
      try {
        const res = await subtasksApi.create(taskId, { title: trimmed });
        const created = res?.data?.data ?? res?.data ?? null;
        if (created?.id) {
          setSubtasks(prev => [...prev, created]);
          return created;
        }
        refresh();
      } catch (e) {
        console.error('[useSubtasks] addSubtask error:', e);
        refresh();
      }
    } else {
      const sub = offlineStorage.addSubtask(taskId, trimmed);
      if (sub) setSubtasks(prev => [...prev, sub]);
      return sub;
    }
  }, [taskId, isOnline, refresh]);

  const toggleSubtask = useCallback(async (subtaskId) => {
    mutatedRef.current = true;

    // Optimistic flip immediately so UI feels instant
    let prevDone = false;
    setSubtasks(prev => {
      const target = prev.find(s => s.id === subtaskId);
      prevDone = target?.done ?? false;
      return prev.map(s =>
        s.id === subtaskId
          ? { ...s, done: !s.done, completed_at: !s.done ? new Date().toISOString() : null }
          : s
      );
    });

    if (isOnline) {
      try {
        // PATCH /tasks/:taskId/subtasks/:subtaskId/toggle
        // Backend flips done and sets completed_at — returns the updated subtask
        const res = await subtasksApi.toggle(taskId, subtaskId);
        const updated = res?.data?.data ?? res?.data ?? null;

        // Settle UI with server truth (has correct done + completed_at from DB)
        if (updated?.id) {
          setSubtasks(prev => prev.map(s => s.id === subtaskId ? updated : s));
        }
      } catch (e) {
        console.error('[useSubtasks] toggleSubtask error:', e);
        // Rollback to original done state on failure
        setSubtasks(prev => prev.map(s =>
          s.id === subtaskId
            ? { ...s, done: prevDone, completed_at: prevDone ? s.completed_at : null }
            : s
        ));
      }
    } else {
      offlineStorage.toggleSubtask(taskId, subtaskId);
    }
  }, [taskId, isOnline]);

  const updateSubtask = useCallback(async (subtaskId, title) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    mutatedRef.current = true;
    setSubtasks(prev => prev.map(s => s.id === subtaskId ? { ...s, title: trimmed } : s));
    if (isOnline) {
      try {
        await subtasksApi.update(taskId, subtaskId, { title: trimmed });
      } catch {
        refresh();
      }
    } else {
      offlineStorage.updateSubtask(taskId, subtaskId, { title: trimmed });
    }
  }, [taskId, isOnline, refresh]);

  const removeSubtask = useCallback(async (subtaskId) => {
    mutatedRef.current = true;
    setSubtasks(prev => prev.filter(s => s.id !== subtaskId));
    if (isOnline) {
      try {
        await subtasksApi.remove(taskId, subtaskId);
      } catch {
        refresh();
      }
    } else {
      offlineStorage.removeSubtask(taskId, subtaskId);
    }
  }, [taskId, isOnline, refresh]);

  const progress = {
    total: subtasks.length,
    done:  subtasks.filter(s => s.done).length,
    pct:   subtasks.length > 0
             ? Math.round((subtasks.filter(s => s.done).length / subtasks.length) * 100)
             : 0,
  };

  return {
    subtasks, loading, progress,
    fetchFromApi,
    addSubtask, toggleSubtask, updateSubtask, removeSubtask, refresh,
  };
}